import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";

import { useLoading } from "../../../../../shared/context/LoadingContext";
import MultiSelectRelatorio from "../../ui/filtrosPesquisa/MultiSelectRelatorio";

const DESFECHOS = ["Atendida", "Dispensada", "Sem resposta", "Em aberto"];

const CORES_DESFECHO = {
  Atendida: "success",
  Dispensada: "default",
  "Sem resposta": "error",
  "Em aberto": "warning",
};

const CartaoResumo = ({ titulo, valor, nota, cor = "primary.main" }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="body2" color="textSecondary">
        {titulo}
      </Typography>
      <Typography variant="h5" sx={{ color: cor, fontWeight: "bold" }}>
        {valor}
      </Typography>
      {nota && (
        <Typography variant="caption" color="textSecondary">
          {nota}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Relatório de urgências de balcão.
 *
 * ─── O NÚMERO QUE IMPORTA É O «SEM RESPOSTA» ────────────────────────────────
 *
 * São as pessoas que chegaram com dor e a quem ninguém chegou — nem atendeu,
 * nem dispensou. O dia acabou e o sistema fechou-as. É uma conversa a ter com
 * a equipa, e só passou a existir quando as urgências deixaram de ser apagadas.
 *
 * Por isso o relatório conta as dispensadas e as sem resposta ao lado das
 * atendidas: um relatório que só contasse as atendidas respondia «correu tudo
 * bem» todos os meses.
 *
 * ─── A ESPERA MÉDIA É SÓ DAS ATENDIDAS ──────────────────────────────────────
 *
 * Numa urgência sem resposta, o momento do fecho é o fim do dia — e isso não é
 * tempo de espera de ninguém. Incluí-la fazia a média parecer catastrófica por
 * uma razão que não tem nada a ver com esperar.
 */
function RelatorioUrgencias({
  dataFiltro,
  clinicaId,
  auth,
  acao,
  sendRequest,
  atualizacoes,
  unidadeSelecionada,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [motivos, setMotivos] = useState([]);
  const [desfechos, setDesfechos] = useState([]);
  const [procura, setProcura] = useState("");
  const [mostrarGrupos, setMostrarGrupos] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  const gerar = useCallback(async () => {
    setErro(null);
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias/relatorio/${clinicaId}`,
        "POST",
        JSON.stringify({
          dataInicio: dataFiltro.dataInicial.format("YYYY-MM-DD"),
          dataFim: dataFiltro.dataFinal.format("YYYY-MM-DD"),
          estabelecimento: unidadeSelecionada || undefined,
          motivo: motivos,
          desfecho: desfechos,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      setDados(resposta);
      setPagina(0);
    } catch (err) {
      setErro(err.message || "Erro ao gerar o relatório");
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, dataFiltro, unidadeSelecionada, motivos, desfechos, auth.token]);

  const exportar = useCallback(() => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const linhas = filtrarLocalmente(dados.urgencias, procura);
    const wb = XLSX.utils.book_new();

    const cabecalho = [
      "Data",
      "Hora",
      "Cliente",
      "Contacto",
      "Motivo",
      "Detalhes",
      "Unidade",
      "Marcada por",
      "Desfecho",
      "Resolvida por",
      "Hora do atendimento",
      "Espera (min)",
      "Dispensada por",
      "Motivo da dispensa",
      "Nota da dispensa",
      "Escalou",
    ];

    const ws = XLSX.utils.aoa_to_sheet([
      ["Relatório de Urgências"],
      [
        `Período: ${dataFiltro.dataInicial.format(
          "DD/MM/YYYY",
        )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      [`Gerado a ${moment().format("DD/MM/YYYY HH:mm")}`],
      [],
      cabecalho,
      ...linhas.map((u) => [
        moment(u.data).format("DD/MM/YYYY"),
        moment(u.data).format("HH:mm"),
        u.cliente?.nome || "-",
        u.cliente?.contacto || "-",
        u.motivo || "-",
        u.descricao || "-",
        u.unidade || "-",
        u.marcadaPor || "-",
        u.desfecho,
        u.atendidaPor || u.dispensadaPor || "-",
        u.atendidaEm ? moment(u.atendidaEm).format("HH:mm") : "-",
        u.esperaMinutos ?? "-",
        u.dispensadaPor || "-",
        u.motivoDispensa || "-",
        u.notaDispensa || "-",
        u.escalou ? `Sim (nível ${u.nivelEscalada})` : "Não",
      ]),
    ]);

    const estiloCabecalho = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "B91C1C" } },
      alignment: { horizontal: "center" },
    };
    cabecalho.forEach((_, i) => {
      const ref = XLSX.utils.encode_cell({ r: 4, c: i });
      if (ws[ref]) ws[ref].s = estiloCabecalho;
    });
    ws["!cols"] = cabecalho.map((c) => ({ wch: Math.max(12, c.length + 3) }));
    XLSX.utils.book_append_sheet(wb, ws, "Urgências");

    const r = dados.resumo;
    const grupo = (titulo, grupos) => [
      [],
      [titulo, "Nº"],
      ...grupos.map((g) => [g.nome, g.total]),
    ];

    const resumo = [
      ["RESUMO"],
      [],
      ["Urgências", r.total],
      ["Atendidas", r.atendidas],
      ["Dispensadas", r.dispensadas],
      ["Sem resposta", r.semResposta],
      ["Em aberto", r.emAberto],
      ["Escalaram (ninguém respondeu a tempo)", r.escalaram],
      [],
      ["Espera média das atendidas (min)", r.esperaMedia ?? "-"],
      ["Maior espera (min)", r.esperaMaxima ?? "-"],
      ...grupo("POR MOTIVO", r.porMotivo),
      ...grupo("POR DESFECHO", r.porDesfecho),
      ...grupo("POR UNIDADE", r.porUnidade),
      ...grupo("POR QUEM ATENDEU", r.porQuemAtendeu),
      ...grupo("POR QUEM MARCOU", r.porQuemMarcou),
      ...grupo("POR MOTIVO DA DISPENSA", r.porMotivoDispensa || []),
    ];

    const wsResumo = XLSX.utils.aoa_to_sheet(resumo);
    wsResumo["A1"].s = { font: { bold: true, sz: 14 } };
    wsResumo["!cols"] = [{ wch: 40 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

    XLSX.writeFile(
      wb,
      `relatorio_urgencias_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
    );
    toast.success("Ficheiro exportado.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, procura, dataFiltro]);

  useEffect(() => {
    if (acao === "gerar") gerar();
    else if (acao === "exportar") exportar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, atualizacoes]);

  const visiveis = useMemo(
    () => filtrarLocalmente(dados?.urgencias || [], procura),
    [dados, procura],
  );
  const inicio = pagina * porPagina;
  const daPagina = visiveis.slice(inicio, inicio + porPagina);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Relatório de Urgências
          </Typography>
          {dados && (
            <Button
              startIcon={<FileDownloadIcon />}
              onClick={exportar}
              variant="contained"
            >
              Exportar Excel
            </Button>
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <MultiSelectRelatorio
              label="Motivo"
              value={motivos}
              onChange={setMotivos}
              opcoes={(dados?.opcoes?.motivos || []).map((m) => ({ valor: m }))}
              chaveDe={(o) => o.valor}
              rotuloDe={(o) => o.valor}
              totalDe={() => undefined}
              largura={220}
            />
            <MultiSelectRelatorio
              label="Desfecho"
              value={desfechos}
              onChange={setDesfechos}
              opcoes={DESFECHOS.map((d) => ({ valor: d }))}
              chaveDe={(o) => o.valor}
              rotuloDe={(o) => o.valor}
              totalDe={() => undefined}
              largura={220}
            />
            <Button variant="contained" onClick={gerar}>
              Aplicar filtros
            </Button>
            <Button
              onClick={() => {
                setMotivos([]);
                setDesfechos([]);
                setProcura("");
              }}
            >
              Limpar
            </Button>
          </Box>
        </Paper>

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {!dados && !erro && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
            Escolha o período e aperte «Gerar Relatório».
          </Typography>
        )}

        {dados && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Urgências"
                  valor={dados.resumo.total}
                  nota={`${dados.resumo.atendidas} atendidas`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Sem resposta"
                  valor={dados.resumo.semResposta}
                  nota="ninguém atendeu nem dispensou"
                  cor="error.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Espera média"
                  valor={
                    dados.resumo.esperaMedia === null
                      ? "—"
                      : `${dados.resumo.esperaMedia} min`
                  }
                  nota={
                    dados.resumo.esperaMaxima === null
                      ? "só das atendidas"
                      : `maior: ${dados.resumo.esperaMaxima} min · só das atendidas`
                  }
                  cor="warning.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Escalaram"
                  valor={dados.resumo.escalaram}
                  nota={`${dados.resumo.dispensadas} dispensadas`}
                  cor="secondary.main"
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">
                  Por motivo, desfecho, unidade e quem atendeu
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setMostrarGrupos((v) => !v)}
                >
                  {mostrarGrupos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={mostrarGrupos}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {[
                    ["Motivo", dados.resumo.porMotivo],
                    ["Desfecho", dados.resumo.porDesfecho],
                    ["Unidade", dados.resumo.porUnidade],
                    ["Quem atendeu", dados.resumo.porQuemAtendeu],
                    ["Quem marcou", dados.resumo.porQuemMarcou],
                    [
                      "Motivo da dispensa",
                      dados.resumo.porMotivoDispensa || [],
                    ],
                  ].map(([titulo, grupos]) => (
                    <Grid item xs={12} md={6} key={titulo}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {titulo}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      {grupos.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Sem dados
                        </Typography>
                      )}
                      {grupos.map((g) => (
                        <Box
                          key={g.nome}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            {g.nome}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {g.total}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>

            <TextField
              fullWidth
              size="small"
              placeholder="Procurar no resultado: cliente, motivo, quem atendeu…"
              value={procura}
              onChange={(e) => {
                setProcura(e.target.value);
                setPagina(0);
              }}
              sx={{ mb: 2 }}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {visiveis.length} urgência(s)
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Motivo</TableCell>
                    <TableCell>Detalhes</TableCell>
                    <TableCell align="center">Desfecho</TableCell>
                    {/* «Resolvida por» e nao «Atendida por»: a mesma coluna
                        mostra quem assumiu E quem dispensou, e quem dispensa
                        nao atendeu ninguem. O cabecalho dizia o contrario do
                        que a linha mostrava. */}
                    <TableCell>Resolvida por</TableCell>
                    <TableCell align="right">Espera</TableCell>
                    <TableCell>Marcada por</TableCell>
                    <TableCell>Unidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daPagina.map((u) => (
                    <TableRow key={u._id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(u.data).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>{moment(u.data).format("HH:mm")}</TableCell>
                      <TableCell>
                        {u.cliente ? (
                          <MuiLink
                            component={Link}
                            to={u.cliente.link}
                            color="inherit"
                            underline="hover"
                          >
                            {u.cliente.nome}
                          </MuiLink>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{u.motivo || "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Tooltip title={u.descricao || ""}>
                          <Typography variant="body2" noWrap>
                            {u.descricao || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            u.desfecho === "Dispensada" && u.motivoDispensa
                              ? `Dispensada · ${u.motivoDispensa}`
                              : u.desfecho
                          }
                          color={CORES_DESFECHO[u.desfecho] || "default"}
                        />
                        {u.escalou && (
                          <Tooltip
                            title={`Escalou — nível ${u.nivelEscalada}`}
                          >
                            <Chip
                              size="small"
                              label="!"
                              color="error"
                              sx={{ ml: 0.5 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.atendidaPor || u.dispensadaPor || "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {u.esperaMinutos === null
                          ? "—"
                          : `${u.esperaMinutos} min`}
                      </TableCell>
                      <TableCell>{u.marcadaPor || "-"}</TableCell>
                      <TableCell>{u.unidade || "-"}</TableCell>
                    </TableRow>
                  ))}

                  {visiveis.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ py: 3 }}
                        >
                          Nenhuma urgência neste período.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={visiveis.length}
              page={pagina}
              onPageChange={(e, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              labelRowsPerPage="Registos por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

/** Procura em tudo o que a linha mostra. */
function filtrarLocalmente(linhas, termo) {
  const t = String(termo || "")
    .trim()
    .toLowerCase();
  if (!t) return linhas;

  return linhas.filter((u) =>
    [
      u.cliente?.nome,
      u.cliente?.contacto,
      u.motivo,
      u.descricao,
      u.desfecho,
      u.atendidaPor,
      u.dispensadaPor,
      u.motivoDispensa,
      u.marcadaPor,
      u.unidade,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(t)),
  );
}

export default RelatorioUrgencias;
